import { FC, useState } from 'react';
import { assignScanner, getPartnerMembers, removeScanner } from '../../services';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { AddScannerModal } from '../AddScannerModal/AddScannerModal';
import { Gate } from '../../types';
import { Icon } from 'features/common/components/Icon';
import { Modal } from 'features/common/components/Modal';
import styled from 'styled-components';
import { useToastNotification } from 'hooks/useToastNotification';
import { useTranslation } from 'react-i18next';
import { airbrake } from 'ErrorBoundary';

interface ScannersModalProps {
	isOpen: boolean;
	onClose: () => void;
	gate: Gate;
	eventId: string;
}

const ModalContent = styled.div`
	padding: 24px;
	width: 600px;
	max-width: 100%;
	background: ${({ theme }) => theme.colors.black};
`;

const HeaderContainer = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 24px;
	padding-bottom: 16px;
	border-bottom: 1px solid ${({ theme }) => theme.colors.whitePearl}22;
`;

const Title = styled.h2`
	margin: 0;
	font-size: 18px;
	font-weight: 500;
	color: ${({ theme }) => theme.colors.whitePearl};
`;

const AddButton = styled.button`
	background: ${({ theme }) => theme.colors.black};
	color: ${({ theme }) => theme.colors.whitePearl};
	border: 1px solid ${({ theme }) => theme.colors.whitePearl}33;
	padding: 8px 16px;
	border-radius: 20px;
	cursor: pointer;
	font-size: 13px;
	display: flex;
	align-items: center;
	gap: 8px;
	transition: all 0.2s ease;

	&:hover {
		background: ${({ theme }) => theme.colors.whitePearl}11;
	}
`;

const ActionButton = styled.button<{ isAssigned?: boolean }>`
	background: ${({ theme, isAssigned }) => (isAssigned ? theme.colors.whitePearl + '22' : theme.colors.black)};
	color: ${({ theme }) => theme.colors.whitePearl};
	border: 1px solid
		${({ theme, isAssigned }) => (isAssigned ? theme.colors.red + '66' : theme.colors.whitePearl + '33')};
	padding: 8px 16px;
	border-radius: 20px;
	cursor: pointer;
	font-size: 13px;
	transition: all 0.2s ease;
	display: flex;
	align-items: center;
	gap: 6px;

	&:hover {
		background: ${({ theme, isAssigned }) => (isAssigned ? theme.colors.red + '22' : theme.colors.whitePearl + '11')};
		border-color: ${({ theme, isAssigned }) => (isAssigned ? theme.colors.red : theme.colors.whitePearl + '33')};
	}

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
`;

const MembersList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	max-height: 400px;
	overflow-y: auto;
	padding-right: 12px;

	&::-webkit-scrollbar {
		width: 8px;
	}

	&::-webkit-scrollbar-track {
		background: transparent;
	}

	&::-webkit-scrollbar-thumb {
		background: ${({ theme }) => theme.colors.whitePearl}22;
		border-radius: 4px;
	}
`;

const MemberItem = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 12px 16px;
	border: 1px solid ${({ theme }) => theme.colors.whitePearl}22;
	border-radius: 8px;
	background: ${({ theme }) => theme.colors.black};
	transition: all 0.2s ease;

	&:hover {
		background: ${({ theme }) => theme.colors.whitePearl}11;
	}
`;

const MemberInfo = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
`;

const MemberName = styled.span`
	font-weight: 500;
	font-size: 14px;
	color: ${({ theme }) => theme.colors.whitePearl};
`;

const MemberEmail = styled.span`
	font-size: 13px;
	color: ${({ theme }) => theme.colors.whitePearl}77;
`;

const ButtonsContainer = styled.div`
	display: flex;
	gap: 8px;
`;

export const ScannersModal: FC<ScannersModalProps> = ({ onClose, gate, eventId }) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const { showNotification, TYPES } = useToastNotification();
	const [loadingScannerId, setLoadingScannerId] = useState<number | null>(null);
	const [isAddScannerModalOpen, setIsAddScannerModalOpen] = useState(false);

	const { data: partnerData } = useQuery({
		queryKey: ['partnerSettings'],
		queryFn: getPartnerMembers,
	});

	const assignScannerMutation = useMutation({
		mutationFn: (scannerId: number) => assignScanner(eventId, gate.ID, scannerId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['gates', eventId] });
			showNotification({ message: t('Scanner assigned successfully'), type: TYPES.SUCCESS });
			setLoadingScannerId(null);
		},
		onError: (error) => {
			airbrake.notify({ error: 'Error assigning scanner', context: { error } });
			showNotification({ message: t('Error assigning scanner'), type: TYPES.ERROR });
			setLoadingScannerId(null);
		},
	});

	const removeScannerMutation = useMutation({
		mutationFn: (scannerId: number) => removeScanner(eventId, gate.ID, scannerId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['gates', eventId] });
			showNotification({ message: t('Scanner removed successfully'), type: TYPES.SUCCESS });
			setLoadingScannerId(null);
		},
		onError: (error) => {
			airbrake.notify({ error: 'Error removing scanner', context: { error } });
			showNotification({ message: t('Error removing scanner'), type: TYPES.ERROR });
			setLoadingScannerId(null);
		},
	});

	const handleToggleScanner = (scannerId: number, isAssigned: boolean) => {
		setLoadingScannerId(scannerId);
		if (isAssigned) {
			removeScannerMutation.mutate(scannerId);
		} else {
			assignScannerMutation.mutate(scannerId);
		}
	};

	const assignedScannerIds = gate.assigned_scanners.map((scanner) => scanner.scanner_id);

	return (
		<>
			<Modal onClose={onClose}>
				<ModalContent>
					<HeaderContainer>
						<Title>
							{t('Manage Scanners for Gate')}: {gate.name}
						</Title>
						<ButtonsContainer>
							<AddButton onClick={() => setIsAddScannerModalOpen(true)}>
								<Icon iconName="plus" size="M" />
								{t('Add Scanner')}
							</AddButton>
							<ActionButton onClick={onClose}>
								<Icon iconName="close" size="M" />
								{t('Close')}
							</ActionButton>
						</ButtonsContainer>
					</HeaderContainer>

					<MembersList>
						{partnerData?.members?.map((member: any) => {
							const isAssigned = assignedScannerIds.includes(member.user_id);
							return (
								<MemberItem key={member.id}>
									<MemberInfo>
										<MemberName>{member.full_name || member.name}</MemberName>
										<MemberEmail>{member.email}</MemberEmail>
									</MemberInfo>
									<ActionButton
										isAssigned={isAssigned}
										onClick={() => handleToggleScanner(member.user_id, isAssigned)}
										disabled={loadingScannerId === member.user_id}
									>
										{loadingScannerId === member.user_id ? (
											<Icon iconName="loading" size="M" />
										) : isAssigned ? (
											<>
												<Icon iconName="close" size="M" />
												{t('Remove')}
											</>
										) : (
											<>
												<Icon iconName="plus" size="M" />
												{t('Assign')}
											</>
										)}
									</ActionButton>
								</MemberItem>
							);
						})}
					</MembersList>
				</ModalContent>
			</Modal>

			<AddScannerModal
				isOpen={isAddScannerModalOpen}
				onClose={() => setIsAddScannerModalOpen(false)}
				eventId={eventId}
			/>
		</>
	);
};
